<template>
  <Transition
    name="lien-panel"
    enter-active-class="lien-panel-enter-active"
    leave-active-class="lien-panel-leave-active"
    enter-from-class="lien-panel-enter-from"
    enter-to-class="lien-panel-enter-to"
    leave-from-class="lien-panel-leave-from"
    leave-to-class="lien-panel-leave-to"
  >
    <div
      v-if="modelValue"
      data-cy="lien-form-panel"
      class="fixed top-20 right-0 bg-white shadow-2xl z-[200] flex flex-col rounded-lg overflow-hidden"
      style="width: 40vw; min-width: 480px; max-width: 700px; height: calc(100vh - 80px);"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white">
        <h2 class="text-lg font-bold text-gray-900">
          {{ (isEditing || loading) ? 'Edit Lien' : 'Add Lien' }}
        </h2>
        <LlSecondaryButton
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close"
          data-cy="lien-panel-close"
          @click="close"
        />
      </div>

      <!-- Scrollable body -->
      <div class="flex-1 overflow-y-auto px-6 py-5">
        <!-- Skeleton while loading lien data -->
        <template v-if="loading">
          <SkeletonRenderer
            :spec="[
              { type: 'list-item', class: 'mb-1' },
              { type: 'list-item', class: 'mb-1' },
              { type: 'row', spacing: 'gap-4', class: 'mb-1', columns: [
                { type: 'list-item', class: 'flex-1' },
                { type: 'list-item', class: 'flex-1' },
              ]},
              { type: 'paragraph', class: 'mb-1' },
              { type: 'row', spacing: 'gap-4', class: 'mb-1', columns: [
                { type: 'list-item', class: 'flex-1' },
                { type: 'list-item', class: 'flex-1' },
              ]},
              { type: 'list-item' },
            ]"
            container-class="space-y-0"
          />
        </template>
        <v-form v-else ref="formRef">
        <!-- Payee -->
        <div class="mb-5">
          <LlAutocomplete
            ref="payeeAutocompleteRef"
            :model-value="form.vendorId"
            :search="vendorSearch"
            label="Payee"
            :is-required="true"
            :items="vendors"
            item-title="displayName"
            item-value="vendorId"
            :loading="vendorsLoading"
            placeholder="Search vendors…"
            hide-details="auto"
            :error-messages="vendorIdError"
            clearable
            data-cy="lien-payee"
            @update:model-value="form.vendorId = $event"
            @update:search="vendorSearch = $event; onVendorSearch($event)"
          >
            <template #no-data>
              <v-list-item>
                <v-list-item-title
                  class="text-sm"
                  :class="vendorError ? 'text-error' : 'text-gray-500'"
                >
                  {{ vendorError || 'No vendors found' }}
                </v-list-item-title>
              </v-list-item>
            </template>
            <template #append-item>
              <v-list-item
                class="text-primary cursor-pointer"
                data-cy="add-new-vendor"
                @click="openAddVendor"
              >
                <v-list-item-title class="font-medium">
                  <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                  {{ vendorSearch ? `Create New "${vendorSearch}"` : 'Add New Vendor' }}
                </v-list-item-title>
              </v-list-item>
            </template>
          </LlAutocomplete>

          <!-- Inline add vendor -->
          <div v-if="showAddVendor" class="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label class="text-sm font-medium text-gray-700 mb-1 block">
              Display Name <span class="font-bold">*</span>
            </label>
            <div class="flex items-center gap-2">
              <LlTextField
                v-model="newVendorName"
                placeholder="e.g., Harris County Hospital"
                hide-details="auto"
                data-cy="new-vendor-name"
                class="flex-1"
              />
              <LlSecondaryButton
                color="primary"
                :loading="creatingVendor"
                data-cy="save-new-vendor"
                @click="saveNewVendor"
              >
                Create
              </LlSecondaryButton>
              <LlSecondaryButton
                data-cy="cancel-new-vendor"
                @click="cancelAddVendor"
              >
                Cancel
              </LlSecondaryButton>
            </div>
          </div>
        </div>

        <!-- Lien Type -->
        <div class="mb-5">
          <LlSelect
            v-model="form.lienType"
            label="Lien Type"
            :is-required="true"
            :items="LIEN_TYPE_OPTIONS"
            item-title="label"
            item-value="id"
            :return-object="false"
            placeholder="Select type"
            hide-details="auto"
            :rules="[(v) => !!v || 'Lien type is required']"
            data-cy="lien-type"
          />
        </div>

        <!-- Original Amount + Negotiated Amount -->
        <div class="grid grid-cols-2 gap-4 mb-5">
          <div>
            <LlTextField
              v-model="form.originalAmount"
              label="Original Amount"
              :is-required="true"
              type="number"
              :min="0.01"
              :step="0.01"
              prefix="$"
              placeholder="0.00"
              hide-details="auto"
              :rules="[(v) => (v !== null && v !== '' && Number(v) > 0) || 'Original amount must be greater than $0']"
              data-cy="lien-original-amount"
            />
          </div>
          <div>
            <LlTextField
              v-model="form.negotiatedAmount"
              label="Negotiated Amount (optional)"
              type="number"
              :min="0"
              :step="0.01"
              prefix="$"
              placeholder="0.00"
              hide-details="auto"
              :rules="negotiatedAmountRules"
              clearable
              data-cy="lien-negotiated-amount"
            />
          </div>
        </div>

        <!-- Savings preview -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="opacity-0 -translate-y-2 max-h-0"
          enter-to-class="opacity-100 translate-y-0 max-h-24"
          leave-from-class="opacity-100 translate-y-0 max-h-24"
          leave-to-class="opacity-0 -translate-y-2 max-h-0"
        >
          <div
            v-if="savingsPreview.show"
            class="mb-5 rounded-lg overflow-hidden"
            data-cy="savings-preview"
          >
            <!-- Positive savings -->
            <div
              v-if="!savingsPreview.over"
              class="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg px-4 py-3 flex items-center justify-between"
            >
              <div class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <LlIcon icon="mdi-piggy-bank-outline" size="18" color="success" />
                </div>
                <div>
                  <div class="text-xs text-green-dark font-medium">Estimated Savings</div>
                  <div class="text-base font-bold text-green-dark tabular-nums leading-tight">
                    {{ formatCurrency(savingsPreview.amount) }}
                  </div>
                </div>
              </div>
              <div class="text-right">
                <div class="text-2xl font-bold text-green-dark tabular-nums leading-none">
                  {{ savingsPreview.percentage }}%
                </div>
                <div class="text-[10px] text-green-dark uppercase tracking-wider font-semibold">reduction</div>
              </div>
            </div>
            <!-- Over budget -->
            <div
              v-else
              class="bg-[rgba(var(--v-theme-error),0.08)] border border-[rgba(var(--v-theme-error),0.3)] rounded-lg px-4 py-3 flex items-center justify-between"
            >
              <div class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-[rgba(var(--v-theme-error),0.08)] flex items-center justify-center">
                  <LlIcon icon="mdi-alert-outline" size="18" color="error" />
                </div>
                <div>
                  <div class="text-xs text-error font-medium">Over Original Amount</div>
                  <div class="text-base font-bold text-error tabular-nums leading-tight">
                    +{{ formatCurrency(savingsPreview.amount) }}
                  </div>
                </div>
              </div>
              <div class="text-right">
                <div class="text-2xl font-bold text-error tabular-nums leading-none">
                  {{ savingsPreview.percentage }}%
                </div>
                <div class="text-[10px] text-error uppercase tracking-wider font-semibold">over</div>
              </div>
            </div>
          </div>
        </Transition>

        <!-- Description -->
        <div class="mb-5">
          <LlTextarea
            v-model="form.description"
            label="Description (optional)"
            :placeholder="descriptionPlaceholder"
            :rows="2"
            hide-details="auto"
            :rules="[(v) => !v || v.length <= 500 || 'Description cannot exceed 500 characters']"
            data-cy="lien-description"
          />
          <div class="flex justify-end mt-1">
            <span
              class="text-xs"
              :class="form.description.length > 500 ? 'text-error' : 'text-gray-400'"
            >{{ form.description.length }}/500</span>
          </div>
        </div>

        <!-- Service date range (start + End Date), plus Recorded / Filing Date. -->
        <div class="grid grid-cols-2 gap-4 mb-1">
          <div>
            <LlDatePicker
              v-model="dateOfServiceDate"
              label="Service Start Date (optional)"
              data-cy="lien-date-of-service"
            />
          </div>
          <div>
            <LlDatePicker
              v-model="dateOfServiceEndDate"
              label="Service End Date (optional)"
              :min="dateOfServiceDate"
              data-cy="lien-date-of-service-end"
            />
          </div>
        </div>
        <div
          v-if="serviceEndDateError"
          class="text-xs text-error mb-4"
          data-cy="lien-date-of-service-end-error"
        >
          {{ serviceEndDateError }}
        </div>
        <div v-else class="mb-5" />

        <div class="grid grid-cols-2 gap-4 mb-5">
          <div>
            <LlDatePicker
              v-model="recordedFilingDateDate"
              label="Recorded / Filing Date (optional)"
              data-cy="lien-recorded-filing-date"
            />
          </div>
        </div>

        <!-- County -->
        <div class="mb-5">
          <LlTextField
            v-model="form.county"
            label="County / Jurisdiction (optional)"
            placeholder="e.g. Denver County"
            hide-details="auto"
            :rules="[(v) => !v || v.length <= 255 || 'County/Jurisdiction is too long (max 255 characters)']"
            data-cy="lien-county"
          />
        </div>
        </v-form>
      </div>

      <!-- Sticky footer (disable buttons while loading) -->
      <div class="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0 mr-16">
        <LlSecondaryButton
          :disabled="saving || loading"
          data-cy="lien-cancel"
          @click="close"
        >
          Cancel
        </LlSecondaryButton>
        <div class="flex flex-col items-end gap-0.5">
          <LlSecondaryButton
            color="primary"
            :loading="saving"
            :disabled="loading || isPushedToQbo"
            data-cy="lien-save"
            @click="submit"
          >
            {{ (isEditing || loading) ? 'Save Changes' : 'Add Lien' }}
          </LlSecondaryButton>
          <span v-if="isPushedToQbo" class="text-xs text-gray-400">Undo push to edit</span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue';
import { useLienStore } from '@/stores/useLienStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { formatCurrency } from '@/services/utils';
import { LIEN_TYPE_OPTIONS } from '@/constants/lienConstants';
import type { LienData, LienType, VendorData, CreateLienRequest } from '@/models/lienTypes';
import { validateServiceDateRange, isPushed } from '@/utils/lienHelpers';
import { parseISO, format, isValid } from 'date-fns';
import LlTextField from '@/components/LlTextField.vue';
import LlTextarea from '@/components/LlTextarea.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlAutocomplete from '@/components/LlAutocomplete.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlIcon from '@/components/LlIcon.vue';
import SkeletonRenderer from '@/components/SkeletonRenderer.vue';
import EventTracker from '@/services/eventTracker';

// ── Types ──────────────────────────────────────────────────────────────────
interface FormState {
  vendorId: string | null;
  lienType: LienType | null;
  originalAmount: number | null;
  negotiatedAmount: number | null;
  description: string;
  dateOfService: string | null;
  dateOfServiceEnd: string | null;
  recordedFilingDate: string | null;
  county: string;
}

// ── Props / Emits ──────────────────────────────────────────────────────────
const props = defineProps<{
  modelValue: boolean;
  matterId: string;
  matterName?: string | null;
  lien?: LienData | null;
  tracking?: object;
  loading?: boolean;
}>();

const emit = defineEmits(['update:modelValue', 'saved']);

// ── Dependencies ───────────────────────────────────────────────────────────
const lienStore = useLienStore();
const { handleError, handleSuccess } = useSnackbarHandler();

// ── Template refs ──────────────────────────────────────────────────────────
const formRef = ref<{ validate: () => Promise<{ valid: boolean }>; resetValidation: () => void } | null>(null);
const payeeAutocompleteRef = ref<InstanceType<typeof LlAutocomplete> | null>(null);

// ── Form state ─────────────────────────────────────────────────────────────
const EMPTY_FORM: FormState = {
  vendorId: null,
  lienType: null,
  originalAmount: null,
  negotiatedAmount: null,
  description: '',
  dateOfService: null,
  dateOfServiceEnd: null,
  recordedFilingDate: null,
  county: '',
};

const form: FormState = reactive({ ...EMPTY_FORM });
const vendorIdError = ref('');
const saving = ref(false);

const isEditing = computed(() => !!props.lien?.lienId);
const isPushedToQbo = computed(() => isPushed(props.lien?.status));

// ── Date helpers (LlDatePicker ↔ ISO string) ──────────────────────────────
function toDate(iso: string | null): Date | null {
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
}
function fromDate(d: Date | null): string | null {
  return d && isValid(d) ? format(d, 'yyyy-MM-dd') : null;
}
const dateOfServiceDate = computed({
  get: () => toDate(form.dateOfService),
  set: (v: Date | null) => { form.dateOfService = fromDate(v); },
});
const dateOfServiceEndDate = computed({
  get: () => toDate(form.dateOfServiceEnd),
  set: (v: Date | null) => { form.dateOfServiceEnd = fromDate(v); },
});
const recordedFilingDateDate = computed({
  get: () => toDate(form.recordedFilingDate),
  set: (v: Date | null) => { form.recordedFilingDate = fromDate(v); },
});

// Inline validation for the optional service date range (end ≥ start).
const serviceEndDateError = computed(() =>
  validateServiceDateRange(form.dateOfService, form.dateOfServiceEnd),
);

// ── Vendor search ──────────────────────────────────────────────────────────
const vendors = ref<VendorData[]>([]);
const vendorsLoading = ref(false);
const vendorSearch = ref('');
const vendorError = ref('');

let vendorSearchTimer: ReturnType<typeof setTimeout> | null = null;

function onVendorSearch(val: string): void {
  if (vendorSearchTimer) clearTimeout(vendorSearchTimer);
  vendorError.value = '';
  vendorSearchTimer = setTimeout(async () => {
    vendorsLoading.value = true;
    try {
      vendors.value = await lienStore.fetchVendors(val);
    } catch {
      vendorError.value = 'Unable to load vendors — check QuickBooks connection in Settings.';
    } finally {
      vendorsLoading.value = false;
    }
  }, 250);
}

// ── Inline add vendor ──────────────────────────────────────────────────────
const showAddVendor = ref(false);
const newVendorName = ref('');
const creatingVendor = ref(false);
const vendorCreatedInline = ref(false);

function openAddVendor(): void {
  payeeAutocompleteRef.value?.$el?.querySelector('input')?.blur();
  newVendorName.value = vendorSearch.value;
  showAddVendor.value = true;
}

function cancelAddVendor(): void {
  showAddVendor.value = false;
  newVendorName.value = '';
}

async function saveNewVendor(): Promise<void> {
  const name = newVendorName.value.trim();
  if (!name) return;
  creatingVendor.value = true;
  try {
    const vendor = await lienStore.addVendor({ displayName: name });
    vendors.value = [...vendors.value, vendor];
    form.vendorId = vendor.vendorId;
    vendorCreatedInline.value = true;
    cancelAddVendor();
    handleSuccess(`Vendor "${vendor.displayName}" created`);
  } catch (e: unknown) {
    handleError(e);
  } finally {
    creatingVendor.value = false;
  }
}

// ── Validation rules ───────────────────────────────────────────────────────
const negotiatedAmountRules = computed(() => [
  (v: number | null | string) =>
    v === null || v === '' || Number(v) >= 0 || 'Negotiated amount cannot be negative',
  (v: number | null | string) =>
    v === null ||
    v === '' ||
    !form.originalAmount ||
    Number(v) <= Number(form.originalAmount) ||
    'Negotiated amount cannot exceed original amount',
]);

// ── Savings preview ────────────────────────────────────────────────────────
const savingsPreview = computed(() => {
  const orig = Number(form.originalAmount) || 0;
  const neg = Number(form.negotiatedAmount) || 0;
  if (orig <= 0 || neg <= 0) return { show: false, over: false, amount: 0, percentage: 0 };

  const diff = orig - neg;
  const percentage = ((Math.abs(diff) / orig) * 100).toFixed(2);
  return {
    show: true,
    over: neg > orig,
    amount: Math.abs(diff),
    percentage,
  };
});

// ── Description placeholder ────────────────────────────────────────────────
const descriptionPlaceholder = computed(() => {
  const vendor = vendors.value.find((v) => v.vendorId === form.vendorId)?.displayName;
  const lienTypeLabel = LIEN_TYPE_OPTIONS.find((o) => o.id === form.lienType)?.label;
  const parts = [props.matterName, vendor, lienTypeLabel].filter(Boolean);
  if (parts.length === 0) return 'e.g. Matter Name – Payee – Type';
  return parts.join(' – ');
});

// ── Watchers ───────────────────────────────────────────────────────────────

// Populate form when editing
watch(
  () => props.lien,
  (lien) => {
    if (lien) {
      form.vendorId = lien.vendorId;
      form.lienType = lien.lienType;
      form.originalAmount = lien.originalAmount;
      form.negotiatedAmount = lien.negotiatedAmount ?? null;
      form.description = lien.description ?? '';
      form.dateOfService = lien.dateOfService?.substring(0, 10) ?? null;
      form.dateOfServiceEnd = lien.dateOfServiceEnd?.substring(0, 10) ?? null;
      form.recordedFilingDate = lien.recordedFilingDate?.substring(0, 10) ?? null;
      form.county = lien.county ?? '';
      if (lien.vendorId && lien.vendorDisplayName) {
        vendors.value = [{ vendorId: lien.vendorId, displayName: lien.vendorDisplayName }];
      }
    } else {
      Object.assign(form, EMPTY_FORM);
    }
  },
  { immediate: true },
);

// Clear payee error when vendor is selected
watch(() => form.vendorId, (id) => { if (id) vendorIdError.value = ''; });

// Reset on open
watch(
  () => props.modelValue,
  (open) => {
    if (!open) return;
    vendorIdError.value = '';
    formRef.value?.resetValidation();
    showAddVendor.value = false;
    newVendorName.value = '';
    vendorCreatedInline.value = false;
    if (!props.lien) {
      Object.assign(form, EMPTY_FORM);
      vendors.value = [];
      vendorSearch.value = '';
    }
    onVendorSearch('');
  },
);

// ── Validate / Submit ──────────────────────────────────────────────────────

function toNullableNumber(v: number | null | string): number | null {
  return v !== null && v !== '' ? Number(v) : null;
}

async function validate(): Promise<boolean> {
  let valid = true;

  const formResult = await formRef.value?.validate?.();
  if (formResult && !formResult.valid) valid = false;

  // LlAutocomplete has no :rules — validate manually
  vendorIdError.value = '';
  if (!form.vendorId) {
    vendorIdError.value = 'Payee is required';
    valid = false;
  }

  // Submit guards (mirror :rules for environments without v-form)
  if (!form.lienType) valid = false;
  if (!form.originalAmount || Number(form.originalAmount) <= 0) valid = false;
  const neg = toNullableNumber(form.negotiatedAmount);
  if (neg !== null) {
    if (neg < 0 || neg > Number(form.originalAmount)) valid = false;
  }
  if (form.description && form.description.length > 500) valid = false;
  if (form.county && form.county.length > 255) valid = false;
  if (serviceEndDateError.value) valid = false;

  return valid;
}

function buildPayload(): CreateLienRequest {
  return {
    vendorId: form.vendorId!,
    lienType: form.lienType!,
    originalAmount: Number(form.originalAmount),
    negotiatedAmount: toNullableNumber(form.negotiatedAmount),
    description: form.description || null,
    county: form.county || null,
    dateOfService: form.dateOfService || null,
    dateOfServiceEnd: form.dateOfServiceEnd || null,
    recordedFilingDate: form.recordedFilingDate || null,
  };
}

function trackEditAnalytics(payload: CreateLienRequest): void {
  if (!props.tracking) return;
  const lien = props.lien!;

  if (Number(lien.originalAmount) !== payload.originalAmount) {
    EventTracker.trackEvent(props.tracking, 'Lien', 'Amount Updated', {
      matterId: props.matterId,
      lienId: lien.lienId,
      field: 'originalAmount',
      oldValue: lien.originalAmount,
      newValue: payload.originalAmount,
    });
  }

  const oldNeg = lien.negotiatedAmount ?? null;
  const newNeg = payload.negotiatedAmount ?? null;
  if (oldNeg !== newNeg) {
    EventTracker.trackEvent(props.tracking, 'Lien', 'Amount Updated', {
      matterId: props.matterId,
      lienId: lien.lienId,
      field: 'negotiatedAmount',
      oldValue: oldNeg,
      newValue: newNeg,
    });
  }
}

function trackCreateAnalytics(payload: CreateLienRequest): void {
  if (!props.tracking) return;
  EventTracker.trackEvent(props.tracking, 'Lien', 'Created', {
    matterId: props.matterId,
    lienType: payload.lienType,
    hasNegotiatedAmount: payload.negotiatedAmount !== null,
    hasCounty: !!payload.county,
    source: 'manual',
    vendorCreatedInline: vendorCreatedInline.value,
  });
  vendorCreatedInline.value = false;
}

async function submit(): Promise<void> {
  if (isPushedToQbo.value) return;
  if (!(await validate())) return;

  saving.value = true;
  try {
    const payload = buildPayload();
    let saved: LienData;

    if (isEditing.value) {
      saved = await lienStore.updateLien(props.lien!.lienId, payload);
      trackEditAnalytics(payload);
    } else {
      saved = await lienStore.createLien(props.matterId, payload);
      trackCreateAnalytics(payload);
    }

    handleSuccess(isEditing.value ? 'Lien updated' : 'Lien added');
    emit('saved', saved);
    close();
  } catch (e: unknown) {
    handleError(e);
  } finally {
    saving.value = false;
  }
}

function close(): void {
  emit('update:modelValue', false);
}
</script>

<!-- Non-scoped so animation classes work when Teleported to body -->
<style>
.lien-panel-enter-active,
.lien-panel-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.lien-panel-enter-from {
  transform: translateX(100%);
}
.lien-panel-enter-to {
  transform: translateX(0);
}
.lien-panel-leave-from {
  transform: translateX(0);
}
.lien-panel-leave-to {
  transform: translateX(100%);
}
</style>
